import { SeasonProvider, useSeason } from './context/SeasonContext';
import { SeasonsPage, MySeasonPage, SeasonOverviewPage, SeasonPublicLayout } from './pages/Seasons/SeasonsPage';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useEffect } from 'react';
import { SideNav } from './components/Sidenav/SideNav';
import { Header } from './components/Header/Header';
import { Profile } from './pages/MyProfile/MyProfile';
import { MyTeam } from './pages/MyTeam/MyTeam';
import { Rating } from './pages/Rating/Rating';
import { MyAchieve } from './pages/MyAchieve/MyAchieve';
import { Transactions } from './pages/Transactions/Transactions';
import { AuctionCatalog, AuctionLot } from './pages/Auction/Auction';
import { SchedulePage } from './pages/Schedule/SchedulePage';
import { KnowledgeBasePage } from './pages/KnowledgeBase/KnowledgeBasePage';
import { Settings } from './pages/Settings/Settings';
import { AdminPage } from './pages/Admin/Admin';
import { SearchPage } from './pages/Search/SearchPage';
import { TeamResultPage } from './pages/Search/TeamResultPage';
import { UserResultPage } from './pages/Search/UserResultPage';
import { SharedProfilePage } from './pages/SharedProfile/SharedProfilePage';
import { LandingPage } from './pages/Landing/LandingPage';
import { NewsPage, NewsArticlePage } from './pages/News/NewsPage';
import { CaseShowcasePage } from './pages/CaseShowcase/CaseShowcasePage';
import { SelectionRosterPage } from './pages/SelectionRoster/SelectionRosterPage';
import { TeamRosterPage } from './pages/TeamRoster/TeamRosterPage';
import { VacanciesPage } from './pages/Vacancies/VacanciesPage';
import { ErrorPage } from './pages/Errors/ErrorPage';
import { Login } from './pages/AuthPage/Login';
import { Register } from './pages/AuthPage/Register';
import { ForgotPassword } from './pages/AuthPage/ForgotPassword';
import { ResetPassword } from './pages/AuthPage/ResetPassword';
import { VerifyEmail } from './pages/AuthPage/VerifyEmail';
import { LeaderIdCallback } from './pages/AuthPage/LeaderIdCallback';
import { SfeduCallback } from './pages/AuthPage/SfeduCallback';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import { useAdminAccess } from './pages/Admin/useAdminAccess';
import './App.css';

const METRIKA_ID = 108330882;

function YandexMetrikaTracker() {
  const location = useLocation();

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.ym !== 'function') {
      return;
    }

    const url = `${window.location.origin}${location.pathname}${location.search}${location.hash}`;
    window.ym(METRIKA_ID, 'hit', url, {
      title: document.title,
      referer: document.referrer,
    });
  }, [location]);

  return null;
}

function MainLayout() {
  const isAdminPage = useLocation().pathname === '/admin';
  const { withSeason } = useSeason();
  const adminAccess = useAdminAccess();
  return (
    <div className="app-layout">
      <SideNav adminAccess={adminAccess} />
      <main className="app-content">
        {!isAdminPage && <Header />}
        <Routes>
          <Route path="/" element={<Navigate to="/profile" replace />} />
          <Route path="/my-season" element={<MySeasonPage />} />
          <Route path="/profile" element={<Profile />} />
          <Route path="/team" element={<MyTeam />} />
          <Route path="/rating" element={<Rating />} />
          <Route path="/achieve" element={<MyAchieve />} />
          <Route path="/transactions" element={<Transactions />} />
          <Route path="/transactions/auction" element={<AuctionCatalog />} />
          <Route path="/transactions/auction/mine" element={<AuctionCatalog mine />} />
          <Route path="/transactions/auction/admin" element={<Navigate to={withSeason('/admin?section=auction')} replace />} />
          <Route path="/transactions/auction/:lotId" element={<AuctionLot />} />
          <Route path="/schedule" element={<SchedulePage />} />
          <Route path="/knowledge" element={<KnowledgeBasePage />} />
          <Route path="/vacancies" element={<VacanciesPage />} />
          <Route path="/settings" element={<Settings />} />
          <Route path="/search" element={<SearchPage />} />
          <Route path="/search/users/:userId" element={<UserResultPage />} />
          <Route path="/search/teams/:teamId" element={<TeamResultPage />} />
          <Route
            path="/error/403"
            element={(
              <ErrorPage
                code="403"
                title="Недостаточно прав"
                description="У вас нет доступа к этой странице. Если считаете, что это ошибка, обратитесь к администратору."
                inApp
              />
            )}
          />
          <Route
            path="/error/500"
            element={(
              <ErrorPage
                code="500"
                title="Внутренняя ошибка"
                description="Что-то пошло не так на сервере. Попробуйте обновить страницу немного позже."
                inApp
              />
            )}
          />
          <Route
            path="/admin"
            element={(
              <AdminRoute access={adminAccess}>
                <AdminPage auctionOnly={adminAccess.auctionOnly} />
              </AdminRoute>
            )}
          />
          <Route
            path="*"
            element={(
              <ErrorPage
                code="404"
                title="Страница не найдена"
                description="Проверьте адрес страницы или перейдите в нужный раздел через меню."
                inApp
              />
            )}
          />
        </Routes>
      </main>
    </div>
  );
}

function ProtectedRoute({ children }) {
  const { loading, isAuthenticated } = useAuth();

  if (loading) {
    return null;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children;
}

function AuthRoute({ children }) {
  const { loading, isAuthenticated } = useAuth();

  if (loading) {
    return null;
  }

  if (isAuthenticated) {
    return <Navigate to="/my-season" replace />;
  }

  return children;
}

function AdminRoute({ children, access }) {
  const { withSeason } = useSeason();
  const location = useLocation();

  if (access.loading) {
    return null;
  }

  if (!access.allowed) {
    return <Navigate to={withSeason('/my-season')} replace />;
  }

  if (access.auctionOnly && new URLSearchParams(location.search).get('section') !== 'auction') {
    return <Navigate to={withSeason('/admin?section=auction')} replace />;
  }

  return children;
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <SeasonProvider>
          <YandexMetrikaTracker />
          <Routes>
            <Route path="/seasons/:slug" element={<SeasonPublicLayout><SeasonOverviewPage /></SeasonPublicLayout>} />
            <Route path="/seasons" element={<SeasonPublicLayout><SeasonsPage /></SeasonPublicLayout>} />
            <Route path="/news" element={<NewsPage />} />
            <Route path="/news/:articleId" element={<NewsArticlePage />} />
            <Route
              path="/"
              element={<LandingPage />}
            />
            <Route
              path="/cases"
              element={<CaseShowcasePage />}
            />
            <Route
              path="/participants"
              element={<SelectionRosterPage />}
            />
            <Route
              path="/teams-public"
              element={<TeamRosterPage />}
            />
            <Route
              path="/profiles/share/:token"
              element={<SharedProfilePage />}
            />
            <Route
              path="/login"
              element={
                <AuthRoute>
                  <Login />
                </AuthRoute>
              }
            />
            <Route
              path="/register"
              element={
                <AuthRoute>
                  <Register />
                </AuthRoute>
              }
            />
            <Route
              path="/forgot-password"
              element={<ForgotPassword />}
            />
            <Route
              path="/reset-password"
              element={<ResetPassword />}
            />
            <Route
              path="/verify-email"
              element={<VerifyEmail />}
            />
            <Route path="/auth/sfedu/complete" element={<SfeduCallback />} />
            <Route
              path="/auth/leader-id/callback"
              element={<LeaderIdCallback />}
            />
            <Route
              path="/leader-id/callback"
              element={<LeaderIdCallback />}
            />
            <Route
              path="/*"
              element={
                <ProtectedRoute>
                  <MainLayout />
                </ProtectedRoute>
              }
            />
          </Routes>
          </SeasonProvider>
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
